import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Sidebar from "./components/Sidebar/Sidebar";
import Dashboard from "./pages/Dashboard";
import UserManagement from "./pages/UserManagement";
import SenderManagement from "./pages/SenderManagement";
import ReceiverManagement from "./pages/ReceiverManagement";
import CustomsAgent from "./pages/CustomsAgent";
import DeclarationManagement from "./pages/DeclarationManagement";
import DeclarationView from "./pages/DeclarationView";
import Success from './pages/Success';
import Payments from './pages/Payments';
import Cancel from './pages/Cancel';
import { TaricJsonErrors } from "./components/Main/TaricJsonErrors";

import { useUserLoaded, useSenders, useAgents, useReceivers, useCompany } from './hooks/useApiHooks';

import logoutIcon from "./assets/logout-icon.png";

import english from "./assets/united-states.png";
import spanish from "./assets/spain.png";

import { ArrowUpLeft, Save, X, FileJson, Settings, ChevronDown } from 'lucide-react';

import "./App.css";
import { gsap } from 'gsap';
import { useDispatch, useSelector, shallowEqual, useStore } from 'react-redux';
import { fetchCreditStats, setDeclarationDirty, clearLastEditedDocumentField } from './store/reducers';
import { applyDocumentFieldToAllPartidas } from './utils/applyToAllDocuments';
import { deepClone } from './utils/dataHelpers';
import { createEditableDataHash } from './utils/hashUtils';
import { saveWorkflowSessions, saveActiveWorkflowSessionId } from './utils/workflowSessionStorage';

import { useDeclarationSummary } from "./hooks/useDeclarationSummary";
import { ValidationErrors } from "./components/Dashboard/DeclarationSummary/ValidationErrors";

import toast from 'react-hot-toast';
import CreditSnippet from './components/CreditSnippet';
import StripePaymentModal from './components/StripePaymentModal';
import { PreferencesModal } from './components/PreferencesModal';
import { useLanguage, getMainTranslation, getDeclarationManagementTranslation } from './hooks/useLanguage';
import { getDeclarationSummaryTranslation, getReviewUserDetailsTranslation } from './hooks/useLanguage';
import { FieldOverviewPanel } from './components/Dashboard/FieldOverviewPanel';
import { AiIconSvg, FieldOverviewIconSvg } from './components/Dashboard/FieldOverviewIcons';
import { GenerateTab } from './components/Main/GenerateTab';
import {
  selectActiveTabEditableData,
  selectActiveTabId,
  selectActiveTabIsDirty
} from './store/selectors/tabSelectors';
import { useDeclarationBreadcrumb } from './contexts/DeclarationBreadcrumbContext';
import { useAllErrors } from './hooks/useValidationErrors';
import { isActiveWorkflowProcessingBusyState } from './utils/workflowProcessingUiUtils';

function Main({ isDarkMode, setIsDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const store = useStore();
  const showBacktoDashboardButton = useSelector(state => state.ui.showBacktoDashboardButton);
  const userData = useSelector((state) => state.user.userData);
  
  // Get active workflow session ID
  const activeWorkflowSessionId = useSelector((state) => state.workflowSessions?.activeSessionId);

  const isActiveWorkflowProcessingBusy = useSelector(isActiveWorkflowProcessingBusyState);
  
  // CRITICAL: Use centralized hook for validation and TARIC JSON errors (per-workflow only)
  const {
    validationErrors,
    taricJsonErrors,
    hasValidationErrors: hasValidationErrorsFlag,
    hasTaricErrors,
    hasAnyErrors: hasErrors,
    activeWorkflowSessionId: hookWorkflowId,
  } = useAllErrors();

  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('allFields');
  
  // CRITICAL: Get extractedData from workflow session state first (priority: workflow session > global state), then normalize to array format
  const extractedData = useSelector(state => {
    // Priority 1: Active workflow session's extractedData
    const workflowSessionsState = state.workflowSessions;
    const currentActiveSessionId = workflowSessionsState?.activeSessionId;
    if (currentActiveSessionId) {
      const workflowExtractedData = workflowSessionsState?.sessions?.[currentActiveSessionId]?.state?.extractedData;
      if (workflowExtractedData) {
        // Normalize to array format
        return Array.isArray(workflowExtractedData) 
          ? workflowExtractedData 
          : (workflowExtractedData && typeof workflowExtractedData === 'object' ? [workflowExtractedData] : []);
      }
    }
    // Priority 2: Fallback to global state
    const globalData = state.documents.extractedData;
    if (globalData) {
      return Array.isArray(globalData) 
        ? globalData 
        : (globalData && typeof globalData === 'object' ? [globalData] : []);
    }
    return null;
  });
  
  const stepsCompleted = useSelector(state => state.documents.stepsCompleted);
  
  // Track typing state to prevent UI updates while user is editing
  const isUserTypingRef = useRef(false);
  const typingTimerRef = useRef(null);
  
  // Track saved editableData to prevent FieldOverviewPanel from updating while typing (only update when Save is clicked)
  const savedEditableDataRef = useRef(null);
  const previousIsDirtyForSaveRef = useRef(null);
  const prevEditableDataHashRef = useRef(null);
  const [savedEditableData, setSavedEditableData] = useState(null);

  const isUserLoaded = useUserLoaded();
  
  useCompany();
  useSenders();
  useReceivers();
  useAgents();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const storedValue = localStorage.getItem('isSidebarCollapsed');
    return storedValue !== null ? JSON.parse(storedValue) : false;
  });

  // Track if user has manually closed the panel despite errors (to prevent auto-expand)
  const userManuallyClosedRef = useRef(false);
  
  // Initialize from localStorage, but will be overridden by error check
  const [isFieldOverviewCollapsed, setIsFieldOverviewCollapsed] = useState(() => {
    const stored = localStorage.getItem('isFieldOverviewCollapsed');
    return stored !== null ? JSON.parse(stored) : true;
  });

  const [isSuggestedCollapsed, setIsSuggestedCollapsed] = useState(false);
  const [isFieldOverviewContentCollapsed, setIsFieldOverviewContentCollapsed] = useState(() => {
    const stored = localStorage.getItem('isFieldOverviewCollapsed');
    return stored !== null ? JSON.parse(stored) : false;
  });

  const [isGenerateTabOpen, setIsGenerateTabOpen] = useState(false);
  const [isGenerateTabCollapsed, setIsGenerateTabCollapsed] = useState(false);

  // When both Suggested and Field Overview are collapsed, collapse the entire panel
  useEffect(() => {
    if (isSuggestedCollapsed && isFieldOverviewContentCollapsed) {
      userManuallyClosedRef.current = true;
      userManuallyOpenedRef.current = false;
      setIsFieldOverviewCollapsed(true);
      localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(true));
    } else {
      setIsFieldOverviewCollapsed(false);
      localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(false));
    }
  }, [isSuggestedCollapsed, isFieldOverviewContentCollapsed]);

  // Check if current route is a declaration route
  const isDeclarationRoute = location.pathname.startsWith('/declaration/') || 
                             location.pathname.startsWith('/declaration-management');

  // Close GenerateTab when navigating away from declaration routes
  useEffect(() => {
    if (!isDeclarationRoute && isGenerateTabOpen) {
      setIsGenerateTabOpen(false);
      setIsGenerateTabCollapsed(false);
      localStorage.setItem('isGenerateTabCollapsed', JSON.stringify(false));
    }
  }, [location.pathname, isDeclarationRoute, isGenerateTabOpen]);

  // Global listener for openGenerateTab event (works even if GenerateTab isn't fully mounted)
  useEffect(() => {
    const handleOpenGenerateTab = () => {
      // Only handle if on a declaration route
      if (isDeclarationRoute) {
        setIsGenerateTabOpen(true);
        setIsGenerateTabCollapsed(false);
        // Collapse sidebar if not already collapsed
        if (!isSidebarCollapsed) {
          setIsSidebarCollapsed(true);
          localStorage.setItem('isSidebarCollapsed', JSON.stringify(true));
        }
      }
    };

    window.addEventListener('openGenerateTab', handleOpenGenerateTab);
    return () => {
      window.removeEventListener('openGenerateTab', handleOpenGenerateTab);
    };
  }, [isDeclarationRoute, isSidebarCollapsed]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(prevState => {
      const newState = !prevState;
      localStorage.setItem('isSidebarCollapsed', JSON.stringify(newState));
      return newState;
    });
  };

  // Auto-expand/collapse field overview panel based on errors
  const prevHasErrorsRef = useRef(null);
  const prevWorkflowRef = useRef(activeWorkflowSessionId);
  const userManuallyOpenedRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const collapseTimeoutRef = useRef(null);
  
  useEffect(() => {
    const workflowChanged = prevWorkflowRef.current !== activeWorkflowSessionId;
    const errorsChanged = prevHasErrorsRef.current !== hasErrors;
    
    
    // Reset manual flags when workflow changes
    if (workflowChanged) {
      prevWorkflowRef.current = activeWorkflowSessionId;
      userManuallyOpenedRef.current = false;
      userManuallyClosedRef.current = false;
      hasInitializedRef.current = false;
      
      // CRITICAL: Always check and expand panel if errors exist when switching workflows
      // Use a delay to ensure errors are loaded from the new workflow
      const timeout = setTimeout(() => {
        if (hasErrors) {
          setIsFieldOverviewCollapsed(false);
          setIsFieldOverviewContentCollapsed(false);
          localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(false));
        } else {
          setIsFieldOverviewCollapsed(true);
          localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(true));
        }
        prevHasErrorsRef.current = hasErrors;
        hasInitializedRef.current = true;
      }, 500);
      
      return () => clearTimeout(timeout);
    }
    
    // Initial mount - check for errors and expand if needed
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      const timeout = setTimeout(() => {
        if (hasErrors) {
          setIsFieldOverviewCollapsed(false);
          setIsFieldOverviewContentCollapsed(false);
          localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(false));
        }
        prevHasErrorsRef.current = hasErrors;
      }, 300);
      return () => clearTimeout(timeout);
    }
    
    // Handle error changes after initialization (errors appeared or cleared)
    if (errorsChanged && hasInitializedRef.current) {
      // Errors appeared - always expand so user sees errors
      if (!prevHasErrorsRef.current && hasErrors) {
        setIsFieldOverviewCollapsed(false);
        setIsFieldOverviewContentCollapsed(false);
        localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(false));
        userManuallyOpenedRef.current = false;
        prevHasErrorsRef.current = hasErrors;
      }
      // Errors cleared - collapse (unless user manually opened it)
      // Use a delay to ensure errors are really cleared (not just temporarily missing)
      else if (prevHasErrorsRef.current && !hasErrors && !userManuallyOpenedRef.current) {
        // Clear any existing collapse timeout
        if (collapseTimeoutRef.current) {
          clearTimeout(collapseTimeoutRef.current);
        }
        
        collapseTimeoutRef.current = setTimeout(() => {
          // Check current state (not closure value) to see if errors are still cleared
          // We'll check this in a separate effect that runs after the delay
          collapseTimeoutRef.current = null;
        }, 500);
        
        prevHasErrorsRef.current = hasErrors;
      } else {
        prevHasErrorsRef.current = hasErrors;
      }
    }
    
    // Cleanup function
    return () => {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current);
        collapseTimeoutRef.current = null;
      }
    };
  }, [hasErrors, activeWorkflowSessionId]);
  
  // When there are errors, always keep panel expanded
  useEffect(() => {
    if (hasErrors && isFieldOverviewCollapsed) {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current);
        collapseTimeoutRef.current = null;
      }
      setIsFieldOverviewCollapsed(false);
      setIsFieldOverviewContentCollapsed(false);
      localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(false));
    }
  }, [hasErrors, isFieldOverviewCollapsed]);
  
  // Handle delayed collapse after errors are cleared
  // This effect runs when hasErrors becomes false and there's no pending timeout
  useEffect(() => {
    // Only collapse if errors are cleared, no timeout is pending, and panel is expanded
    if (!hasErrors && !collapseTimeoutRef.current && isFieldOverviewCollapsed === false && 
        prevHasErrorsRef.current === true && !userManuallyOpenedRef.current && hasInitializedRef.current) {
      // Set a timeout to collapse after delay
      collapseTimeoutRef.current = setTimeout(() => {
        // Double-check errors are still cleared
        if (!hasErrors) {
          setIsFieldOverviewCollapsed(true);
          localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(true));
          userManuallyClosedRef.current = false;
          collapseTimeoutRef.current = null;
        } else {
          collapseTimeoutRef.current = null;
        }
      }, 500);
    }
    
    return () => {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current);
      }
    };
  }, [hasErrors, isFieldOverviewCollapsed]);

  // Listen for sidebar collapse events from ProcessingModal
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      if (event.detail && event.detail.collapsed !== undefined) {
        setIsSidebarCollapsed(event.detail.collapsed);
      }
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => {
      window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
    };
  }, []);

  // Listen for FieldOverview collapse events
  useEffect(() => {
    const handleFieldOverviewCollapse = (event) => {
      if (event.detail && event.detail.collapsed !== undefined) {
        setIsFieldOverviewCollapsed(event.detail.collapsed);
        localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(event.detail.collapsed));
      }
      if (event.detail && event.detail.expandContent === true) {
        setIsFieldOverviewContentCollapsed(false);
      }
    };

    window.addEventListener('fieldOverviewCollapse', handleFieldOverviewCollapse);
    return () => {
      window.removeEventListener('fieldOverviewCollapse', handleFieldOverviewCollapse);
    };
  }, []);

  // Animation
  useEffect(() => {
    const sections = document.querySelectorAll('.fadeUp');
    const tl = gsap.timeline();

    sections.forEach(section => {
      tl.to(section,
        {
          stagger: 0.15,
          duration: 0.15,
          opacity: 1,
          y: 0,
          ease: "back",
        }
      );
    });
  }, [location]);


  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = sessionStorage.getItem('token');
      const expires = sessionStorage.getItem('expires');
      const refreshToken = sessionStorage.getItem('refresh_token');

      if (!token) {
        navigate("/login");
        return;
      } 
      
      if (expires) {
        const expirationTime = parseInt(expires, 10);
        const currentTime = new Date().getTime();
        // Calculate time until expiry (for potential future use) - const timeUntilExpiry = expirationTime - currentTime;

        // Check if the token has expired
        if (currentTime >= expirationTime) {
          // Token is expired - proactively refresh it
          if (!refreshToken) {
            handleLogout();
            return;
          } else {
            // Proactively refresh the token and wait for it to complete
            try {
              const { refreshAccessToken } = await import('./utils/api');
              const data = await refreshAccessToken();
              if (!data) {
                // Check if refresh token still exists (another concurrent refresh might have succeeded)
                const stillHasRefreshToken = sessionStorage.getItem('refresh_token');
                if (stillHasRefreshToken) {
                  // Check if we now have a valid token
                  const newToken = sessionStorage.getItem('token');
                  if (newToken) {
                    return;
                  }
                }
                handleLogout();
                return;
              } else {
                // Token refreshed, continue with app
              }
            } catch (error) {
              // Check if refresh token still exists (another concurrent refresh might have succeeded)
              const stillHasRefreshToken = sessionStorage.getItem('refresh_token');
              if (stillHasRefreshToken) {
                const newToken = sessionStorage.getItem('token');
                if (newToken) {
                  return;
                }
              }
              handleLogout();
              return;
            }
          }
        } else {
          const timeout = expirationTime - currentTime;
          
          const logoutTimer = setTimeout(async () => {
            // On expiration, proactively refresh the token
            const currentRefreshToken = sessionStorage.getItem('refresh_token');
            if (!currentRefreshToken) {
              handleLogout();
            } else {
              try {
                const { refreshAccessToken } = await import('./utils/api');
                const data = await refreshAccessToken();
                if (!data) {
                  // Check if refresh token still exists (another concurrent refresh might have succeeded)
                  const stillHasRefreshToken = sessionStorage.getItem('refresh_token');
                  if (stillHasRefreshToken) {
                    // Check if we now have a valid token
                    const newToken = sessionStorage.getItem('token');
                    if (newToken) {
                      return;
                    }
                  }
                  handleLogout();
                } else {
                }
              } catch (error) {
                // Check if refresh token still exists (another concurrent refresh might have succeeded)
                const stillHasRefreshToken = sessionStorage.getItem('refresh_token');
                if (stillHasRefreshToken) {
                  const newToken = sessionStorage.getItem('token');
                  if (newToken) {
                    return;
                  }
                }
                handleLogout();
              }
            }
          }, timeout);

          return () => {
            clearTimeout(logoutTimer);
          };
        }
      } else {
      }
    };

    checkAndRefreshToken();
  }, []); // Empty dependency array - only run once on mount 

  const [showOptions, setShowOptions] = useState(false);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [backtoDashboardClicked, setBacktoDashboardClicked] = useState(false);
  const [scrollToID, setScrollToID] = useState("");
  

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('expires');
    sessionStorage.removeItem('isDarkMode');
    sessionStorage.removeItem('email');
    sessionStorage.removeItem('user_id');
    navigate('/login');
    toast.success(getMainTranslation('successfullyLoggedOut', isSpanish));
  };

  const handleBacktoDashboard = () => {
    navigate("/");
    if (location.pathname === "/user-management") {
      setScrollToID("review-user");
    } else if (location.pathname === "/sender-management") {
      setScrollToID("review-user");
    } else if (location.pathname === "/receiver-management") {
      setScrollToID("review-user");
    } else {
      setScrollToID("review-agents");
    }
    setBacktoDashboardClicked(true);
  };

  const [shouldShowButton, setShouldShowButton] = useState(false);

  useEffect(() => {
    setShouldShowButton(showBacktoDashboardButton && (location.pathname !== "/"));
  }, [showBacktoDashboardButton, location]);

  const [isSpanish, setIsSpanish] = useLanguage();

  const [showHeaderShadow, setShowHeaderShadow] = useState(false);
  const [isScrolledPastWorkflowTabs, setIsScrolledPastWorkflowTabs] = useState(false);
  const [isScrolledPastPageHeader, setIsScrolledPastPageHeader] = useState(false);
  const [navScrollSegment, setNavScrollSegment] = useState(null);

  const declarationBreadcrumb = useDeclarationBreadcrumb();
  const declarationExportIndex = declarationBreadcrumb?.exportIndex ?? -1;
  const declarationItemIndex = declarationBreadcrumb?.itemIndex ?? -1;

  const activeWorkflowName = useSelector((state) => {
    const id = state?.workflowSessions?.activeSessionId;
    return id ? state?.workflowSessions?.sessions?.[id]?.name || null : null;
  });

  // Refs to track previous values for console logging
  const prevIsScrolledPastWorkflowTabsRef = useRef(false);
  const prevIsScrolledPastPageHeaderRef = useRef(false);

  useEffect(() => {
    let timeoutId;
    let content;
    let cleanup;
    
    const setupScrollListener = () => {
      content = document.getElementById('analysis-report');
      if (!content) {
        timeoutId = setTimeout(setupScrollListener, 100);
        return;
      }
      
      const handleScroll = () => {
        const scrollTop = content.scrollTop || 0;
        
        // Workflow tabs check (only on dashboard)
        if (location.pathname === '/') {
          const containerRect = content.getBoundingClientRect();
          const step1Element = document.getElementById('step-1');
          const workflowTabsContainer = document.getElementById('workflow-tabs-container');
          const targetElement = step1Element || workflowTabsContainer;
          
          if (targetElement) {
            const elementRect = targetElement.getBoundingClientRect();
            // Check if element is scrolled past (element top is above container top)
            const isPast = elementRect.top < containerRect.top + 120;
            
            if (prevIsScrolledPastWorkflowTabsRef.current !== isPast) {
              prevIsScrolledPastWorkflowTabsRef.current = isPast;
              setIsScrolledPastWorkflowTabs(isPast);
            }
          }

          // Update nav breadcrumb from section that is at or just past the top offset
          const sections = content.querySelectorAll('[data-nav-breadcrumb]');
          const offsetTop = 120;
          let currentSegment = null;
          let bestTop = -Infinity;
          sections.forEach((el) => {
            const rect = el.getBoundingClientRect();
            const top = rect.top - containerRect.top;
            if (top <= offsetTop && top > bestTop) {
              bestTop = top;
              currentSegment = el.getAttribute('data-nav-breadcrumb');
            }
          });
          setNavScrollSegment(currentSegment);
        } else {
          // Page header check (non-dashboard pages)
          const pageHeader = document.querySelector('.page-header');
          if (pageHeader) {
            const headerRect = pageHeader.getBoundingClientRect();
            const containerRect = content.getBoundingClientRect();
            // Check if header is scrolled past (header top is above container top)
            const isPast = headerRect.top < containerRect.top + 120;
            
            if (prevIsScrolledPastPageHeaderRef.current !== isPast) {
              prevIsScrolledPastPageHeaderRef.current = isPast;
              setIsScrolledPastPageHeader(isPast);
            }
          }
          setNavScrollSegment(null);
        }
      };
      
      // Initial check
      handleScroll();
      
      // Add scroll listener with passive option for better performance
      content.addEventListener('scroll', handleScroll, { passive: true });
      
      cleanup = () => {
        if (content) {
          content.removeEventListener('scroll', handleScroll);
        }
      };
    };

    setupScrollListener();
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (cleanup) {
        cleanup();
      }
    };
  }, [location.pathname, activeWorkflowName]);

  const navBreadcrumb = useMemo(() => {
    if (location.pathname !== '/') return null;
    const workflowName = activeWorkflowName || 'Workflow';
    if (!navScrollSegment) return null;
    if (navScrollSegment === 'Review Details') {
      if (declarationExportIndex >= 0) {
        let crumb = `${workflowName} > Export #${declarationExportIndex + 1}`;
        if (declarationItemIndex >= 0) crumb += ` > Item #${declarationItemIndex + 1}`;
        return crumb;
      }
      const reviewDetailsLabel = getReviewUserDetailsTranslation('reviewDetails', isSpanish);
      return `${workflowName} > ${reviewDetailsLabel}`;
    }
    const baseCrumb = `${workflowName} > ${navScrollSegment}`;
    const isExportSegment = /^Export(ación)? #\d+$/i.test(navScrollSegment);
    if (isExportSegment && declarationItemIndex >= 0) {
      return `${baseCrumb} > Item #${declarationItemIndex + 1}`;
    }
    return baseCrumb;
  }, [location.pathname, activeWorkflowName, navScrollSegment, declarationExportIndex, declarationItemIndex, isSpanish]);

  const { current, usage, exportCost, loading, error } = useSelector(state => state.creditStats);

  useEffect(() => {
    dispatch(fetchCreditStats());
  }, [dispatch]);

  // CRITICAL: Get editableData from workflow session state first, then tabs, then global state (priority: workflow session > tab > global). Must be declared BEFORE useEffect that uses it. OPTIMIZED: Use raw selector and memoize to prevent creating new array references
  const editableDataRaw = useSelector(state => {
    // Priority 1: Active workflow session's editableData
    const workflowSessionsState = state.workflowSessions;
    const currentActiveSessionId = workflowSessionsState?.activeSessionId;
    if (currentActiveSessionId) {
      const workflowEditableData = workflowSessionsState?.sessions?.[currentActiveSessionId]?.state?.editableData;
      if (workflowEditableData) {
        return workflowEditableData;
      }
    }
    // Priority 2: Active tab's editableData
    const tabsState = state.tabs;
    if (tabsState && tabsState.activeTabId && tabsState.tabs[tabsState.activeTabId]) {
      return tabsState.tabs[tabsState.activeTabId].editableData;
    }
    // Priority 3: Fallback to global state
    return state.documents.editableData;
  });
  
  // Normalize to array format - memoized to prevent new references when data hasn't changed
  const editableData = useMemo(() => {
    if (!editableDataRaw) return [];
    return Array.isArray(editableDataRaw) 
      ? editableDataRaw 
      : (editableDataRaw && typeof editableDataRaw === 'object' ? [editableDataRaw] : []);
  }, [editableDataRaw]);
  
  const lastEditedDocumentField = useSelector(state => state.documents.lastEditedDocumentField);
  const lastEditedDocumentValue = useSelector(state => state.documents.lastEditedDocumentValue);
  const lastEditedDocumentDeclarationIndex = useSelector(state => state.documents.lastEditedDocumentDeclarationIndex);
  const lastEditedDocumentPartidaIndex = useSelector(state => state.documents.lastEditedDocumentPartidaIndex);
  const lastEditedDocumentDocumentIndex = useSelector(state => state.documents.lastEditedDocumentDocumentIndex);
  
  // Get active tab ID and isDirty state
  const activeTabId = useSelector(selectActiveTabId);
  const activeTabIsDirty = useSelector(selectActiveTabIsDirty);
  const globalIsDirty = useSelector(state => state.documents.isDirty);
  const isDirty = activeTabIsDirty || globalIsDirty; // Use active tab's isDirty, fallback to global
  
  // Track workflow changes for savedEditableData reset
  const prevActiveWorkflowSessionIdRef = useRef(activeWorkflowSessionId);
  
  // Memoize extractedData for FieldOverviewPanel - only use savedEditableData (updates on save)
  const fieldOverviewExtractedData = useMemo(() => {
    // CRITICAL: Only use savedEditableData (updates on save) to avoid expensive recalculations during typing. Fallback priority: savedEditableData > editableData > extractedData (ensures data shows on page refresh when savedEditableData hasn't been initialized yet)
    let dataToUse = savedEditableData;
    
    if (!dataToUse) {
      // Try editableData first (more likely to have user's current edits)
      if (editableData && editableData.length > 0) {
        dataToUse = editableData;
      } 
      // Fallback to extractedData
      else if (extractedData && extractedData.length > 0) {
        dataToUse = extractedData;
      } else {
        dataToUse = null;
      }
    }
    
    // Normalize to array format - return null if no data (panel will show "No data available")
    if (!dataToUse) {
      return null;
    }
    
    // Always create new array reference to ensure FieldOverviewPanel detects changes
    const normalized = Array.isArray(dataToUse) 
      ? [...dataToUse] 
      : (dataToUse && typeof dataToUse === 'object' ? [{ ...dataToUse }] : []);
    
    return normalized;
  }, [savedEditableData, editableData, extractedData, activeWorkflowSessionId]);
  
  
  // CRITICAL: Reset savedEditableData when workflow changes
  useEffect(() => {
    if (prevActiveWorkflowSessionIdRef.current !== activeWorkflowSessionId) {
      // Workflow changed - reset saved data to prevent stale data from previous workflow
      savedEditableDataRef.current = null;
      setSavedEditableData(null);
      previousIsDirtyForSaveRef.current = null;
      prevActiveWorkflowSessionIdRef.current = activeWorkflowSessionId;
    }
  }, [activeWorkflowSessionId]);
  
  // CRITICAL: Track saved editableData - only update when Save is clicked (isDirty goes from true to false). Optimized with hash comparison to avoid unnecessary deepClone
  useEffect(() => {
    const currentHash = editableData ? createEditableDataHash(editableData) : null;
    const prevHash = prevEditableDataHashRef.current;
    
    // Initialize saved data on first load or after processing completes (when extractedData exists but savedEditableData doesn't) - ensure editableData has actual data before initializing
    const normalizedEditableData = editableData ? (Array.isArray(editableData) ? editableData : [editableData]) : [];
    const hasEditableData = normalizedEditableData.length > 0;
    if (savedEditableDataRef.current === null && hasEditableData && extractedData && extractedData.length > 0) {
      const dataToSave = normalizedEditableData;
      savedEditableDataRef.current = deepClone(dataToSave);
      setSavedEditableData(dataToSave);
      prevEditableDataHashRef.current = currentHash;
      previousIsDirtyForSaveRef.current = isDirty;
      return;
    }
    
    // Detect when save happens (isDirty goes from true to false)
    if (previousIsDirtyForSaveRef.current === true && isDirty === false && editableData) {
      const dataToSave = Array.isArray(editableData) ? editableData : [editableData];
      // Always update savedEditableData state to trigger FieldOverviewPanel update - only deepClone if hash changed (data actually changed) to avoid unnecessary work
      if (currentHash !== prevHash) {
        savedEditableDataRef.current = deepClone(dataToSave);
        setSavedEditableData(dataToSave);
      } else {
        // Hash hasn't changed, but still update state reference so FieldOverviewPanel detects save - create new array reference to ensure React detects the change
        const newDataRef = [...dataToSave];
        savedEditableDataRef.current = newDataRef;
        setSavedEditableData(newDataRef);
      }
      prevEditableDataHashRef.current = currentHash;
    }
    
    previousIsDirtyForSaveRef.current = isDirty;
  }, [isDirty, editableData, extractedData]);
  
  // Debug: Log isDirty state only when it actually changes (using refs to track previous values)
  const prevIsDirtyRef = useRef(null);
  const prevShouldShowButtonsRef = useRef(null);
  
  useEffect(() => {
    const shouldShowButtons = ((isDirty && extractedData) || (lastEditedDocumentField && extractedData));
    const isDirtyChanged = prevIsDirtyRef.current !== isDirty;
    const shouldShowButtonsChanged = prevShouldShowButtonsRef.current !== shouldShowButtons;
    
    if (isDirtyChanged || shouldShowButtonsChanged) {
      prevIsDirtyRef.current = isDirty;
      prevShouldShowButtonsRef.current = shouldShowButtons;
    }
  }, [activeTabId, activeTabIsDirty, globalIsDirty, isDirty, extractedData, editableData, lastEditedDocumentField]);
  
  // enabled if field was edited 
  const canApplyToAll = lastEditedDocumentField && lastEditedDocumentDeclarationIndex !== null && 
    lastEditedDocumentPartidaIndex !== null && lastEditedDocumentDocumentIndex !== null && editableData;
  
  const hasMultiplePartidas = canApplyToAll && editableData?.[lastEditedDocumentDeclarationIndex]?.partidas?.length > 1;
  const hasMultipleDocuments = canApplyToAll && 
    editableData?.[lastEditedDocumentDeclarationIndex]?.partidas?.[lastEditedDocumentPartidaIndex]?.documentos?.length > 1;
  
  const isApplyButtonEnabled = canApplyToAll && (hasMultiplePartidas || hasMultipleDocuments);
  
  // Track when user is actively typing to prevent tab switching (must be after editableData is declared)
  useEffect(() => {
    // If editableData changes, user might be typing - set a flag that clears after user stops typing
    if (editableData && editableData.length > 0) {
      isUserTypingRef.current = true;
      
      // Clear existing timer
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
      
      // Mark as not typing after 1 second of no changes (longer than validation debounce 800ms to ensure validation completes)
      typingTimerRef.current = setTimeout(() => {
        isUserTypingRef.current = false;
      }, 1000);
    }
    
    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, [editableData]);
  
  const {
    saveChanges: originalSaveChanges,
    cancelChanges,
    setEditableData: updateEditableData
  } = useDeclarationSummary(extractedData);
  
  // Wrap saveChanges to reset the validation errors ref after save
  const saveChanges = () => {
    const result = originalSaveChanges();
    if (result?.success === false) {
      toast.error(getMainTranslation('fixAllRequiredBeforeSave', isSpanish));
      // Expand field overview so user can see the errors
      setIsFieldOverviewCollapsed(false);
      setIsFieldOverviewContentCollapsed(false);
      localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(false));
      return;
    }
    toast.success(getMainTranslation('changesSaved', isSpanish));
    // Clear the last edited document field so "Apply to All" button disappears
    dispatch(clearLastEditedDocumentField());
    // CRITICAL: Persist workflow sessions to sessionStorage immediately so accepted suggestions and form data survive refresh
    const currentState = store.getState();
    const currentWorkflowSessions = currentState?.workflowSessions;
    if (currentWorkflowSessions?.sessions && currentWorkflowSessions?.sessionOrder) {
      saveWorkflowSessions(
        currentWorkflowSessions.sessions,
        currentWorkflowSessions.sessionOrder,
        currentWorkflowSessions.activeSessionId,
        true
      );
      if (currentWorkflowSessions.activeSessionId) {
        saveActiveWorkflowSessionId(currentWorkflowSessions.activeSessionId);
      }
    }
  };

  const handleCancelChanges = () => {
    // Clear lastEditedDocumentField FIRST to immediately hide buttons
    dispatch(clearLastEditedDocumentField());
    // Then cancel changes (which will also clear isDirty)
    cancelChanges();
    toast.success(getMainTranslation('changesCancelled', isSpanish));
  };
  
  // Handler to apply document field changes to all documents
  const handleApplyToAllDocuments = () => {
    // Early validation
    if (!lastEditedDocumentField || 
        lastEditedDocumentDeclarationIndex === null || 
        lastEditedDocumentPartidaIndex === null || 
        lastEditedDocumentDocumentIndex === null) {
      return;
    }
    
    // Get latest editableData from store (workflow session or global state)
    const state = store.getState();
    const workflowSession = state?.workflowSessions?.sessions?.[state?.workflowSessions?.activeSessionId];
    const latestEditableData = Array.isArray(workflowSession?.state?.editableData)
      ? workflowSession.state.editableData
      : (state?.documents?.editableData || editableData);
    
    if (!Array.isArray(latestEditableData)) return;
    
    // Get value to apply (prefer Redux value, fallback to source document)
    const valueToApply = lastEditedDocumentValue ?? 
      latestEditableData[lastEditedDocumentDeclarationIndex]?.partidas?.[lastEditedDocumentPartidaIndex]
        ?.documentos?.[lastEditedDocumentDocumentIndex]?.[lastEditedDocumentField];
    
    if (valueToApply === undefined) return;
    
    // Apply changes
    const updatedData = applyDocumentFieldToAllPartidas(
      latestEditableData,
      lastEditedDocumentDeclarationIndex,
      lastEditedDocumentPartidaIndex,
      lastEditedDocumentDocumentIndex,
      lastEditedDocumentField,
      valueToApply
    );
    
    if (updatedData === latestEditableData) return;
    
    updateEditableData(updatedData);
    dispatch(setDeclarationDirty(true));
    dispatch(clearLastEditedDocumentField());
  };

  if (sessionStorage.getItem('token') && !isUserLoaded) {
    return (
      <div className="loading-container absolute right-0 left-0 bottom-0 top-0 flex items-center justify-center flex-col gap-2">
        <div className="mr-3 w-5 h-5 flex flex-col">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p>{getMainTranslation('loadingYourData', isSpanish)}</p>
      </div>
    );
  }

  return (
    <div className="App">
      {sessionStorage.getItem('token') && (
        <>
          
          <main>
            <Sidebar
              isDarkMode={isDarkMode}
              setIsDarkMode={setIsDarkMode}
              isSidebarCollapsed={isSidebarCollapsed}
              setIsSidebarCollapsed={toggleSidebar}
              isScrolledPastWorkflowTabs={isScrolledPastWorkflowTabs}
              isScrolledPastPageHeader={isScrolledPastPageHeader}
              navBreadcrumb={navBreadcrumb}
              activeWorkflowName={activeWorkflowName}
            />
            <div className={`content min-h-screen max-h-screen overflow-y-auto ${isDarkMode ? "dark-mode" : ""} py-10 pt-0 ${isSidebarCollapsed && "content-sidebar-collapsed"} ${taricJsonErrors ? "taric-json-error px-12 pr-14" : "px-12 pr-14"} ${(isFieldOverviewCollapsed || location.pathname === '/success' || location.pathname === '/cancel') ? "pr-0 transition-all duration-300 ease-in-out mr-0" : "mr-72"}`} id="analysis-report">
              {/* <div className={`fixed top-0 w-10 h-10 bg-secondary ${isSidebarCollapsed ? "left-20" : "left-[250px]"}`}  style={{ borderBottomRightRadius: '3rem'}}>
                <div className="size-full bg-white" style={{borderTopLeftRadius: '3rem', borderBottomRightRadius: '3rem'}}></div>
              </div> */}
              <div className={`${location.pathname === "/" && fieldOverviewExtractedData && fieldOverviewExtractedData.length > 0 ? (isFieldOverviewCollapsed ? "right-[4.75rem]a right-[0]" : "right-72") : "right-8"} ${location.pathname !== "/" && "right-8" } ${isGenerateTabOpen && !isGenerateTabCollapsed ? "right-[22rem]" : ""} top-0 z-[9] flex justify-end fixed gap-2 pt-4 pb-4 pr-4 w-full bg-secondary nav-header-container bg-white-force ${(location.pathname === "/" ? !isScrolledPastWorkflowTabs : !isScrolledPastPageHeader) && "bg-white-force"}`} style={{ borderBottomRightRadius: '0rem'}}>
                <div className="flex justify-end gap-2">
                {((isDirty && extractedData) || (lastEditedDocumentField && extractedData)) && (
                  <div className="flex gap-2 py-2">
                    {isDirty && extractedData && (
                      <>
                        <button
                          className="flex items-center py-2 px-2 gap-2"
                          onClick={handleCancelChanges}
                          title="Cancel Changes"
                        >
                          <X className="size-4" />
                          <span className="text-xs">{getDeclarationSummaryTranslation('cancel', isSpanish)}</span>
                        </button>
                        <button
                          className="svg-force-white bg-primary rounded-full flex items-center py-2 px-6 gap-2"
                          onClick={saveChanges}
                          title="Save Changes"
                        >
                          <Save className="size-4" />
                          <span className="text-xs">{getDeclarationSummaryTranslation('save', isSpanish)}</span>
                        </button>
                      </>
                    )}
                    {/* Apply to All Documents button - only show when a document field has been edited */}
                    {isApplyButtonEnabled && extractedData && (
                      <button
                        className="svg-force-white bg-primary rounded-full flex items-center py-2 px-6 gap-2"
                        onClick={handleApplyToAllDocuments}
                        title={getDeclarationSummaryTranslation('saveAllDocumentsTooltip', isSpanish)}
                      >
                        <Save className="size-4" />
                        <span className="text-xs">{getDeclarationSummaryTranslation('saveAllDocuments', isSpanish)}</span>
                      </button>
                    )}
                  </div>
                )}
                {/* Export to Taric button - only show on /declaration/:id route */}
                {location.pathname.startsWith('/declaration/') && (
                  <div className={`flex gap-2 bg-white rounded-full p-2 ${showHeaderShadow ? 'shadow-xl' : ''}`}>
                    <button
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('openGenerateTab'));
                      }}
                      className="bg-default rounded-full flex items-center gap-2 py-2 px-6"
                      title={getDeclarationManagementTranslation('exportToTaric', isSpanish)}
                    >
                      <FileJson className="size-5 stroke-white" />
                      <span className="text-white text-xs">
                        {getDeclarationManagementTranslation('exportToTaric', isSpanish)}
                      </span>
                    </button>
                  </div>
                )}
                <div className={`h-14 p-2 rounded-full flex justify-end gap-2 transition-shadow ${showHeaderShadow ? 'shadow-xl' : ''}`} id="review-extracted-data2">
                <CreditSnippet
                    current={current}
                    usage={usage}
                    exportCost={exportCost}
                    loading={loading}
                    error={error}
                    isStripeModalOpen={isStripeModalOpen}
                    setIsStripeModalOpen={setIsStripeModalOpen}
                />
                <button
                onClick={() => { setIsSpanish(!isSpanish) }}
                className={`cursor-pointer h-10 flex items-center gap-2 py-2 px-4 rounded-full transition-colors ${isSpanish ? "bg-yellow-100" : "bg-blue-100"}`}
              >
              <img src={isSpanish ? spanish : english} alt="Logo" className="h-5" /> 
                <p className={`text`}>{isSpanish ? getMainTranslation('esp', isSpanish) : getMainTranslation('eng', isSpanish)}</p>
              </button>
                  <div className="circle bg-default mb-10 cursor-pointer" onClick={toggleOptions}>
                    <span className="text-white">{userData?.first_name?.charAt(0).toUpperCase() || 'U'}</span>
                  </div>
                  {showOptions && (
                    <div className="options rounded-2xl bg-white p-6 flex flex-col items-center shadow-md">
                      <div className="!w-20 !h-20 circle bg-default mb-4 cursor-pointer" 
                      onClick={toggleOptions}>
                        <span className="text-white text-3xl">{userData?.first_name?.charAt(0).toUpperCase() || 'U'}</span>
                      </div>
                      <h1 className="font-bold">{userData?.first_name} {userData?.last_name}</h1>
                      <p className="opacity-75 text-sm">{sessionStorage.getItem('email')}</p>
                      <div className="divider mt-8"></div>
                      <span className='options-item flex-1 w-full mt-4' onClick={() => {
                        setIsPreferencesModalOpen(true);
                        setShowOptions(false);
                      }}>
                        <Settings className="!w-fit-content" height={20} />
                        <p className='text-sm'>{getMainTranslation('preferences', isSpanish)}</p>
                      </span>
                      <span className='options-item flex-1 w-full mt-2' onClick={handleLogout}>
                        <img
                          src={logoutIcon}
                          alt="Logout"
                          className="profile-icon" />
                        <p className='text-sm'>{getMainTranslation('logout', isSpanish)}</p>
                      </span>
                    </div>
                  )}
                </div>
                </div>
              </div>
              <Routes>
                <Route path="/" element={<Dashboard
                  showBacktoDashboardButton={showBacktoDashboardButton}
                  setBacktoDashboardClicked={setBacktoDashboardClicked}
                  backtoDashboardClicked={backtoDashboardClicked}
                  scrollToID={scrollToID}
                  setIsStripeModalOpen={setIsStripeModalOpen}
                />} />
                <Route path="/user-management" element={<UserManagement />} />
                <Route path="/sender-management" element={<SenderManagement />} />
                <Route path="/receiver-management" element={<ReceiverManagement />} />
                <Route path="/customs-agent" element={<CustomsAgent />} />
                <Route path="/declaration-management" element={<DeclarationManagement />} />
                <Route 
                  path="/declaration/:id" 
                  element={<DeclarationView />
                  } 
                />
                <Route path="/success" element={<Success />} />
                <Route path="/credit-history" element={<Payments />} />
                <Route path="/cancel" element={<Cancel />} />
              </Routes>
              {shouldShowButton && (
                <button className="bg-default py-4 px-8 rounded-full dashboard-button flex gap-2" onClick={handleBacktoDashboard}>
                  <ArrowUpLeft className='w-6 h-6 stroke-white' />
                  <p className="text-white" >{getMainTranslation('goBackToHome', isSpanish)}</p>
                </button>
              )}
            </div>
            
            {location.pathname === "/" &&
              fieldOverviewExtractedData &&
              fieldOverviewExtractedData.length > 0 &&
              !isActiveWorkflowProcessingBusy && (
              <>
                {isFieldOverviewCollapsed ? (
                  <div className="fixed bottom-4 right-4 flex flex-col gap-2 z-999 w-72">
                    <button
                      type="button"
                      onClick={() => { setIsFieldOverviewCollapsed(false); setIsSuggestedCollapsed(false); }}
                      className="w-full flex items-center gap-2 rounded-full py-3 px-5 cursor-pointer bg-secondary text-left"
                      title={getMainTranslation('suggested', isSpanish)}
                    >
                      <AiIconSvg className="size-5 flex-shrink-0 field-svg-icon" />
                      <span className="text-lg font-semibold whitespace-nowrap truncate">{getMainTranslation('suggested', isSpanish)}</span>
                      <ChevronDown className="size-4 flex-shrink-0 -rotate-90 ml-auto" />
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsFieldOverviewCollapsed(false); setIsFieldOverviewContentCollapsed(false); }}
                      className="w-full flex items-center gap-2 rounded-full py-3 px-5 cursor-pointer bg-secondary text-left"
                      title={getMainTranslation('fieldOverview', isSpanish)}
                    >
                      <FieldOverviewIconSvg className="size-5 flex-shrink-0 field-svg-icon" />
                      <span className="text-lg font-semibold whitespace-nowrap truncate">{getMainTranslation('fieldOverview', isSpanish)}</span>
                      <ChevronDown className="size-4 flex-shrink-0 -rotate-90 ml-auto" />
                    </button>
                  </div>
                ) : (
                  <FieldOverviewPanel
                    extractedData={fieldOverviewExtractedData}
                    activeSubTab={activeSubTab}
                    setActiveSubTab={setActiveSubTab}
                    isSpanish={isSpanish}
                    validationErrors={validationErrors}
                    taricJsonErrors={taricJsonErrors}
                    ValidationErrors={ValidationErrors}
                    TaricJsonErrors={TaricJsonErrors}
                    location={location}
                    activeWorkflowSessionId={activeWorkflowSessionId}
                    isFieldOverviewCollapsed={isFieldOverviewCollapsed}
                    setIsFieldOverviewCollapsed={(collapsed) => {
                      if (collapsed && hasErrors) {
                        userManuallyClosedRef.current = true;
                        userManuallyOpenedRef.current = false;
                      } else if (!collapsed) {
                        userManuallyClosedRef.current = false;
                        userManuallyOpenedRef.current = true;
                      } else {
                        userManuallyOpenedRef.current = false;
                      }
                      setIsFieldOverviewCollapsed(collapsed);
                      localStorage.setItem('isFieldOverviewCollapsed', JSON.stringify(collapsed));
                    }}
                    isSuggestedCollapsed={isSuggestedCollapsed}
                    setIsSuggestedCollapsed={setIsSuggestedCollapsed}
                    isFieldOverviewContentCollapsed={isFieldOverviewContentCollapsed}
                    setIsFieldOverviewContentCollapsed={setIsFieldOverviewContentCollapsed}
                  />
                )}
              </>
            )}

            {/* Generate Tab - Right Side Panel - Only show on declaration routes */}
            {isDeclarationRoute && (
              <GenerateTab 
                isSidebarCollapsed={isSidebarCollapsed}
                setIsSidebarCollapsed={setIsSidebarCollapsed}
                setIsGenerateTabOpen={setIsGenerateTabOpen}
                setIsGenerateTabCollapsed={setIsGenerateTabCollapsed}
                isGenerateTabOpen={isGenerateTabOpen}
              />
            )}
          </main>
          {isStripeModalOpen && (
            <StripePaymentModal isOpen={isStripeModalOpen} onClose={() => setIsStripeModalOpen(false)} />
          )}
          {isPreferencesModalOpen && (
            <PreferencesModal isOpen={isPreferencesModalOpen} onClose={() => setIsPreferencesModalOpen(false)} />
          )}
        </>
      )}
    </div>
  );
}

export default Main;